import api from 'api/axiosInstance';
import { peppolDeactivationGlobalWarning } from 'components/molecules/peppolDeactivationGlobalWarning';
import {
  DocumentExchangeTypeEnum,
  IntegrationsStatusEnum,
  PeppolCurrentIntegration,
  RegistrationOptions,
} from 'types/integrations';
import { IUser } from 'types/users.types';
import { getUserData } from '../users/authentication';
import {
  CompanyStatus,
  getVerificationData,
  IdentityStatus,
} from '../users/verifications';

const service = '/v2/integrations/peppol';

export const getPeppolIntegration = () => {
  return api
    .get<any, { integration: PeppolCurrentIntegration }>(service, {
      ignoreToast: true,
    })
    .then(({ integration }) => {
      return {
        ...integration,
        documentExchangeType:
          integration.documentExchangeType ||
          DocumentExchangeTypeEnum.send_and_receive,
      };
    })
    .catch((error) => {
      const isNotFound = error?.response?.status === 404;
      if (isNotFound) return null;
      throw error;
    });
};

export const createPeppolIntegration = (
  options: RegistrationOptions,
): Promise<PeppolCurrentIntegration> => {
  return api
    .post<any, { integration: PeppolCurrentIntegration }>(service, options)
    .then(({ integration }) => integration);
};

export const patchPeppolIntegration = (
  id: string,
  options: RegistrationOptions,
): Promise<PeppolCurrentIntegration> => {
  return api
    .patch<any, { integration: PeppolCurrentIntegration }>(
      `${service}/${id}/document-exchange-type`,
      options,
    )
    .then(({ integration }) => integration);
};

export const deletePeppolIntegration = (id: string) => {
  return api.delete(`${service}/${id}`);
};

type PeppolDeps = Partial<
  Pick<IUser, 'firstName' | 'lastName' | 'VATNumber' | 'companyName'>
>;

const normalize = (value: any) => {
  if (value == null) return null;
  if (typeof value === 'string') {
    return value.trim() || null;
  }
  return value;
};

export const warnUserAboutPeppolDeactivationBeforeProceeding = async (
  deps: PeppolDeps,
): Promise<void> => {
  if (!deps) return Promise.reject('No data provided');

  const { firstName, lastName, VATNumber, companyName } = deps;

  const maybeChangingIdentity =
    firstName !== undefined || lastName !== undefined;

  const maybeChangingCompany =
    VATNumber !== undefined || companyName !== undefined;

  if (!maybeChangingIdentity && !maybeChangingCompany) return;

  const { peppolIntegration, verificationData } = await Promise.all([
    getPeppolIntegration(),
    getVerificationData(),
    getUserData(),
  ]).then(([peppolIntegration, verificationData, userData]) => {
    const profileBased = {
      tag: 'profile',
      company: {
        status: CompanyStatus.approved,
        verifiedData: {
          vatNumber: userData?.VATNumber || undefined,
          companyName: userData?.companyName || undefined,
        },
      },
      identity: {
        status: IdentityStatus.approved,
        verifiedData: {
          firstName: userData?.firstName,
          lastName: userData?.lastName,
        },
      },
    };

    const verificationBased = {
      tag: 'verification',
      company: {
        status: verificationData?.company?.status,
        verifiedData: {
          vatNumber: verificationData?.company?.verifiedData?.vatNumber,
          companyName: verificationData?.company?.verifiedData?.companyName,
        },
      },
      identity: {
        status: verificationData?.identity?.status,
        verifiedData: {
          firstName: verificationData?.identity?.verifiedData?.firstName,
          lastName: verificationData?.identity?.verifiedData?.lastName,
        },
      },
    };
    return {
      peppolIntegration,
      verificationData: verificationData ? verificationBased : profileBased,
    };
  });

  if (!peppolIntegration) return;

  const isPeppolInactive =
    peppolIntegration.status === IntegrationsStatusEnum.inactive;

  if (isPeppolInactive) return;

  if (verificationData.tag === 'profile') {
    const isPeppolPending =
      peppolIntegration.status === IntegrationsStatusEnum.pending;

    const isPeppolActive =
      peppolIntegration.status === IntegrationsStatusEnum.active;

    if (!isPeppolPending && !isPeppolActive) return;
  }

  const { identity, company } = verificationData;

  const shouldCheckIdentity =
    IdentityStatus.approved === identity.status ||
    IdentityStatus.submitted === identity.status ||
    IdentityStatus.review === identity.status;

  const shouldCheckCompany = CompanyStatus.approved === company.status;

  const changedFields: string[] = [];

  if (shouldCheckIdentity) {
    const { firstName, lastName } = identity.verifiedData;
    if (normalize(deps.firstName) !== normalize(firstName)) {
      changedFields.push('firstName');
    }
    if (normalize(deps.lastName) !== normalize(lastName)) {
      changedFields.push('lastName');
    }
  }

  if (shouldCheckCompany) {
    const { vatNumber, companyName } = company.verifiedData;

    if (normalize(deps.VATNumber) !== normalize(vatNumber)) {
      changedFields.push('VATNumber');
    }
    if (normalize(deps.companyName) !== normalize(companyName)) {
      changedFields.push('companyName');
    }
  }

  if (changedFields.length) {
    // trigger modal
    return peppolDeactivationGlobalWarning({
      id: 'peppol-deactivation-modal',
      fieldNames: changedFields,
    });
  }
};

export const checkPeppolParticipant = (): Promise<{
  registeredIdentifiers: { identifier: string; scheme: string }[];
}> => {
  return api.get(`${service}/lookup-participant`);
};
